/**
 * Magic Link Sent Component
 * 
 * Displays confirmation message after magic link is sent with countdown timer.
 * Memoized for performance optimization.
 */

import { useEffect, memo } from "react";

import { Mail, Clock } from "lucide-react";

import { Button } from "@/components/ui/button";

import type { AuthState, AuthAction } from "@/types/state";

interface MagicLinkSentProps {
  state: AuthState;
  dispatch: (action: AuthAction) => void;
  onResend: () => void;
  onBack: () => void;
}

const MagicLinkSentComponent = ({ state, dispatch, onResend, onBack }: MagicLinkSentProps) => {
  // Countdown timers
  useEffect(() => {
    if (state.resendCooldown > 0) {
      const timer = setInterval(() => {
        dispatch({ type: 'DECREMENT_RESEND_COOLDOWN' });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [state.resendCooldown, dispatch]);

  useEffect(() => {
    if (state.magicLinkCountdown > 0) {
      const timer = setInterval(() => {
        dispatch({ type: 'DECREMENT_MAGIC_LINK_COUNTDOWN' });
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [state.magicLinkCountdown, dispatch]);

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="space-y-4">
      <div className="text-center space-y-4">
        <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center">
          <Mail className="h-8 w-8 text-primary" />
        </div>
        
        <div>
          <h3 className="text-lg font-semibold">Check your email</h3>
          <p className="text-sm text-muted-foreground mt-2">
            We sent a verification link to <strong>{state.email}</strong>
          </p>
          <p className="text-sm text-muted-foreground mt-1">
            Click the link in the email to verify your account.
          </p>
        </div>

        {state.magicLinkCountdown > 0 && (
          <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
            <Clock className="h-4 w-4" />
            <span>Link expires in {formatTime(state.magicLinkCountdown)}</span>
          </div>
        )}
      </div>

      <div className="space-y-2">
        <Button
          onClick={onResend}
          disabled={state.resendCooldown > 0 || state.loading}
          className="w-full"
          variant="outline"
        >
          {state.resendCooldown > 0 
            ? `Resend in ${state.resendCooldown}s` 
            : 'Resend verification link'}
        </Button>

        <Button
          onClick={onBack}
          variant="ghost"
          className="w-full"
          disabled={state.loading}
        >
          Back to sign in
        </Button>
      </div>

      <div className="text-center text-xs text-muted-foreground">
        <p>Didn't receive the email? Check your spam folder.</p>
      </div>
    </div>
  );
};

export const MagicLinkSent = memo(MagicLinkSentComponent);
